import { Box, Typography } from "@mui/material";
import {
  FocusContext,
  useFocusable,
} from "@noriginmedia/norigin-spatial-navigation";
import styled from "@emotion/styled";
import { useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { getOptimizedImageForType } from "../../../utils/cms.utils";
import ContentTag from "../../../utils/tag";
import { getAgeRangePublicRoute } from "../../../utils/ageRange.util";
import { setAutoResumeCookie } from "../../../utils/autoResumeCookie";
import { useInputMode } from "../../../utils/hooks/useInputMode";
import OptimizedImage from "../../common/OptimizedImage";
import { FOCUS_BORDER } from "../../../config/focusStyles";
import LazyRow from "../../common/LazyRow";

// Number of items per row
var ITEMS_PER_ROW = 5;

/**
 * Chunks an array into smaller arrays of specified size
 */
function chunkArray(array, size) {
  var result = [];
  for (var i = 0; i < array.length; i += size) {
    result.push(array.slice(i, i + size));
  }
  return result;
}

const ContentRowWrapper = styled(Box)`
  position: relative;
`;

const RowContainer = styled(Box)`
  display: flex;
  width: 100%;
`;

const MovieItem = ({ item = {}, onEnterPress }) => {
  const { images, tags, createdAt, contentKey } = item;
  const { focused, ref, focusSelf } = useFocusable({
    onEnterPress,
    focusKey: "content_" + contentKey + "_" + createdAt,
  });

  const { setMouseFocus } = useInputMode();
  const handleMouseEnter = useCallback(() => {
    setMouseFocus(true);
    focusSelf();
  }, [focusSelf, setMouseFocus]);

  // Use optimized image with LQIP and WebP support (w=200, q=90)
  const imageData = getOptimizedImageForType("verticalCover", images, "TV", { width: 350, quality: 90, format: 'jpeg' });

  return (
    <Box
      ref={ref}
      onMouseEnter={handleMouseEnter}
      style={{
        width: "20%",
        minWidth: "160px",
        padding: "12px",
        boxSizing: "border-box",
      }}
    >
      <Box
        sx={{
          width: "100%",
          paddingTop: "140%",
          position: "relative",
          overflow: "hidden",
          border: focused ? FOCUS_BORDER.active : FOCUS_BORDER.inactive,
          zIndex: focused ? 10 : 0,
          borderRadius: "16px",
        }}
        onClick={() => onEnterPress?.()}
        className="cursor-pointer"
      >
        <OptimizedImage
          src={imageData.src}
          alt={item.title}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            borderRadius: 'inherit',
          }}
        />
        {tags?.[0] && <ContentTag label={tags?.[0]} />}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.5)",
            opacity: focused ? 1 : 0,
            pointerEvents: "none",
            transition: "opacity 0.2s",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            px: 2,
            flexDirection: "column",
            gap: "1rem",
            zIndex: 10,
            borderRadius: "inherit",
          }}
        >
          <Typography
            sx={{
              color: "white",
              fontSize: "20px",
              fontWeight: 700,
              textAlign: "center",
            }}
          >
            {item.title}
          </Typography>
          <Typography
            sx={{
              color: "white",
              fontSize: "16px",
              fontWeight: 400,
              textAlign: "center",
            }}
          >
            {item.year}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

/**
 * Single row of movie items with its own focus context
 */
const MovieRow = ({ items, onFocus, navigate, isWatchHistory = false }) => {
  const { ref, focusKey } = useFocusable({ onFocus });

  /**
   * Get the navigation handler for a watch history item
   * For watch history, we navigate directly to the player with autoResume cookie
   */
  const getWatchHistoryNavHandler = (contentMeta, contentKey, options) => {
    const kind = contentMeta?.content?.meta?.bin?.kind?.kind;
    const seasonNumber = options?.seasonNumber || options?.season;
    const episodeNumber = options?.episodeNumber || options?.episode;

    // Set autoResume cookie before navigation
    setAutoResumeCookie({
      slug: contentKey,
      season: seasonNumber ? String(seasonNumber) : null,
      episode: episodeNumber ? String(episodeNumber) : null,
    });

    // Navigate directly to player
    if ((kind === 'series' || kind === 'program') && seasonNumber && episodeNumber) {
      // Series/Program: /watch/{slug}/{season}/{episode}
      navigate(`/watch/${contentKey}/${seasonNumber}/${episodeNumber}`);
    } else {
      // Movie: /watch/{slug}
      navigate(`/watch/${contentKey}`);
    }
  };

  /**
   * Get the navigation handler for a regular content item
   * Navigates to the content detail page
   */
  const getContentNavHandler = (contentMeta, contentKey) => {
    navigate(`/${contentMeta?.content?.meta?.bin?.kind?.kind}/${contentKey}`);
  };

  return (
    <FocusContext.Provider value={focusKey}>
      <RowContainer ref={ref}>
        {items.map(({ _id, createdAt, contentMeta, contentKey, options }) => (
          <MovieItem
            item={{
              ageRange: contentMeta?.content?.ageRange,
              ...contentMeta?.content?.meta?.bin,
              contentMeta,
              contentKey,
              _id,
            }}
            key={createdAt + "_" + contentKey}
            onEnterPress={() => {
              // Check if this is a watch history item (has elapsedTime)
              // and navigate directly to player with autoResume
              if (isWatchHistory && options?.elapsedTime > 0) {
                getWatchHistoryNavHandler(contentMeta, contentKey, options);
              } else {
                getContentNavHandler(contentMeta, contentKey);
              }
            }}
          />
        ))}
      </RowContainer>
    </FocusContext.Provider>
  );
};

export const MoviesSlider = ({ movies, onFocus, isWatchHistory: isWatchHistoryProp }) => {
  const ageRange = getAgeRangePublicRoute();
  const _items = movies.filter(
    (x) =>
      x.content?.ageRange <= ageRange ||
      x.contentMeta?.content?.ageRange <= ageRange ||
      x.ageRange <= ageRange
  );
  const { ref, focusKey } = useFocusable();

  const navigate = useNavigate();

  // Auto-detect watch history if not explicitly provided
  // Watch history items have options.elapsedTime
  const isWatchHistory = isWatchHistoryProp !== undefined
    ? isWatchHistoryProp
    : movies.some(item => item.options?.elapsedTime > 0);

  // Chunk items into rows
  const chunkedItems = useMemo(
    function () {
      if (!_items || _items.length === 0) return [];
      return chunkArray(_items, ITEMS_PER_ROW);
    },
    [_items]
  );

  if (_items.length < 1) return null;

  return (
    <FocusContext.Provider value={focusKey}>
      <ContentRowWrapper ref={ref}>
        {chunkedItems.map((rowItems, rowIndex) => (
          <LazyRow key={"row_" + rowIndex} index={rowIndex}>
            <MovieRow
              items={rowItems}
              onFocus={onFocus}
              navigate={navigate}
              isWatchHistory={isWatchHistory}
            />
          </LazyRow>
        ))}
      </ContentRowWrapper>
    </FocusContext.Provider>
  );
};
